import { MoleculePaginationPageItem, MoleculePaginationProps, MoleculePaginationSettings } from "./m-pagination.data";

/**
 * A base class for preparing data for components which can present themselves differently, based on some initial data conditions
 * This is useful for when you want the render a component, with the presentation logic on the front end
 */
export abstract class ComponentDataBuilder<T> {
	/** A holder for the composed component data */
	public data: T;
	/** Optional initial data, merge with the initial data */
	protected initialData: T | Partial<T> | undefined;

	constructor(defaultData: T, initialData?: T | Partial<T>) {
		// store the initial data, if needed later
		this.initialData = initialData;
		// merge the default data with some initial state
		this.data = Object.assign(
			{}, defaultData, (initialData || {})
		);
	}
	/** Blueprint for building component data */
	protected abstract build(): T;
}

/**
 * Build the data needed to render this molecule, based on some optional conditions
 * @param initialData?: MoleculePaginationProps
 */
export class MoleculePaginationBuilder extends ComponentDataBuilder<MoleculePaginationProps> {

	/** The components default settings, which can be extended by initial data passed in */
	static readonly default: MoleculePaginationProps = {
		paginationSettings: {
			totalResults: 0,
			resultsPerPage: 10,
			totalPagesCount: 1,
			currentPage: 1,
		},
		nextPrev: {
			next: '?page=2',
			prev: '',
		},
		pages: [
			{
				page: 1,
				href: '#',
				disabled: true,
			},
		],
	};

	/**
	 * The number of page links to show, e.g. 5 => 1 ... 4 | 5 | 6 ... 10
	 * Untested with numbers other than 5!
	 */
	private readonly defaultNumberOfPageLinks = 5;
	/** The number of middle links to show (e.g. not including page 1 and the final page) */
	private readonly numberOfMiddlePages = this.defaultNumberOfPageLinks - 2;

	constructor(initialData?: Partial<MoleculePaginationProps>) {
		super(MoleculePaginationBuilder.default, initialData);
		this.build();
	}

	/**
	 * build the component data, depending on optional initial data
	 * @returns MoleculePaginationProps
	 */
	protected build(): MoleculePaginationProps {
		if (!this.initialData) {
			// return the default component state
			return this.data;
		}

		// customise the component, based on initial data
		if (this.data.paginationSettings) {
			const { currentPage, totalPagesCount } = this.data.paginationSettings;

			// build the next/prev
			const nextPrev: MoleculePaginationProps['nextPrev'] = {
				next: currentPage < totalPagesCount ? '?page=' + (currentPage + 1) : '',
				prev: currentPage > 1 ? '?page=' + (currentPage - 1) : '',
			};

			// finally, build the component
			const component: MoleculePaginationProps = {
				paginationSettings: this.data.paginationSettings,
				nextPrev,
				pages: this.data.pages
			};
			this.data = component;
		}

		return this.data; // the default
	}


	/**
	 * build the page data, based on the pagination settings and current page
	 * @param  {MoleculePaginationSettings} settings
	 * @returns MoleculePaginationProps
	 */
	private getPagesList(settings: MoleculePaginationSettings): MoleculePaginationProps['pages'] {
		const { totalResults, resultsPerPage, currentPage, totalPagesCount } = settings;
		// console.log(settings);

		// calculate the number of pages, based on the results per page and total results
		// @todo - difference with totalPagesCount?
		const numberOfPages = Math.ceil(totalResults / resultsPerPage);
		// the number of links which will actually show, based on the pages available
		let numberOfMiddleLinksShown: number = totalPagesCount - this.numberOfMiddlePages + 1;
		numberOfMiddleLinksShown = numberOfMiddleLinksShown > this.numberOfMiddlePages ? this.numberOfMiddlePages : numberOfMiddleLinksShown;
		// console.log(numberOfMiddleLinksShown);
		const pages: MoleculePaginationProps['pages'] = [];

		// page 1
		pages.push(this.getPageItem({
			page: 1,
			href: '',
			isActive: currentPage === 1,
		}));

		// spacer after 1, if needed
		if (
			currentPage > this.numberOfMiddlePages &&
			numberOfPages > this.defaultNumberOfPageLinks
		) {
			pages.push(this.getPageItem({
				page: null,
				href: '',
				disabled: true,
			}));
		}


		for (let i = 0; i < this.numberOfMiddlePages; i++) {

			// only build middle pages if there are any
			if (i + this.numberOfMiddlePages > totalPagesCount) {
				break;
			}

			if (currentPage <= this.numberOfMiddlePages) {
				// at the start
				// console.log('Start');
				pages.push(this.getPageItem({
					page: i + 2,
					href: '',
					// disabled: currentPage === i + 2,
					// isActive: currentPage === i + 2,
				}));
			} else if (currentPage + this.numberOfMiddlePages > numberOfPages) {
				// console.log('End');
				// at the end
				// sometimes not all 3 middle pages are shown, if not enough results
				const diff = this.numberOfMiddlePages - numberOfMiddleLinksShown;

				pages.push(this.getPageItem({
					page: numberOfPages - this.numberOfMiddlePages + i + diff,
					href: '',
					// disabled: currentPage === i + 2,
					isActive: currentPage === numberOfPages - i - this.numberOfMiddlePages,
				}));


			} else {
				// console.log('Middle');
				// in the middle
				pages.push(this.getPageItem({
					page: currentPage + (i - 1),
					href: '',
					disabled: currentPage === (i - 1),
					isActive: currentPage === (i - 1),
				}));

			}

		}

		if (
			numberOfPages > this.defaultNumberOfPageLinks &&
			currentPage + this.numberOfMiddlePages <= totalPagesCount
		) {
			// spacer just before the end
			pages.push(this.getPageItem({
				page: null,
				href: '',
				disabled: true,
			}));

		}

		if (totalPagesCount > 1) {
			// append the final page to the end
			pages.push(this.getPageItem({
				page: totalPagesCount,
				href: '',
				isActive: currentPage === totalPagesCount,
			}));
		}

		return pages;
	}

	/**
	 * Get a pagination page item, based on settings
	 * @param  {MoleculePaginationPageItem} pageItem
	 * @returns MoleculePaginationPageItem
	 */
	private getPageItem(pageItem: MoleculePaginationPageItem): MoleculePaginationPageItem {
		let page: MoleculePaginationPageItem;

		if (pageItem.disabled) {
			page = {
				href: '#',
				disabled: true,
				page: null,
				isActive: pageItem.isActive || false,
			};
		} else {
			page = {
				href: '#',
				page: pageItem.page,
				isActive: pageItem.isActive || false,
			};
		}
		return page;
	}

}

// RENDER LOGIC...!


// TO_SHOW = 5


// Examples: (11 pages total)
// Page 1:  (1) 2 3 4 ... 11
// Page 2:  1 (2) 3 4 ... 11
// Page 3:  1 2 (3) 4 ... 11
// Page 4:  1 ... 3 (4) 5 ... 11
// Page 5:  1 ... 4 (5) 6 ... 11
// Page 11: 1 ... 8 9 10 (11)


// Up to TO_SHOW pages, on page i (simple - active page is shown)
// 1 page
// Page (1)
// 2 pages
// Page 1 (2)
// 3 pages
// Page 1 2 (3)
// 4 pages
// Page 1 2 3 (4)
// 5 pages
// Page 1 2 3 4 (5)



// TO_SHOW + 1 pages
// 6 pages, page 1
// Page (1) 2 3 4 ... (6)

// 6 pages, page 2
// Page 1 (2) 3 4 ... 6

// 6 pages, page 3
// Page 1 2 (3) 4 ... 6

// 6 pages, page 4
// Page 1 ... 3 (4) 5 6

// 6 pages, page 5
// Page 1 ... 3 4 (5) 6

// 6 pages, page 6
// Page 1 ... 3 4 5 (6)



// TO_SHOW + 2 pages (or more)

// 7 pages, page 1
// Page (1) 2 3 4 ... (7)

// 7 pages, page 2
// Page 1 (2) 3 4 ... 7

// 7 pages, page 3
// Page 1 2 (3) 4 ... 7

// 7 pages, page 4
// Page 1 ... 3 (4) 5 ... 7

// 7 pages, page 5
// Page 1 ... 4 (5) 6 ... 7

// 7 pages, page 6
// Page 1 ... 4 5 (6) ... 7
